import { Button, Spinner } from '@consigli/facade';
import {
  useExportToExcelMutation,
  useLazyGetFindingsQuery,
  usePackageId,
  useProject,
  useProjectId,
} from '@consigli/hooks';
import { Finding, FindingStatus, ScheduleCheckType } from '@consigli/types';
import { MIME_TYPES, saveBlobBase64 } from '@consigli/utils';
import clsx from 'clsx';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { createScheduleCheckExcelExport } from '../utils/schedule-check-excel-export';

interface ScheduleCheckExportButtonProps {
  className?: string;
}

export const ScheduleCheckExportButton: FC<ScheduleCheckExportButtonProps> = ({ className }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { project } = useProject(projectId);
  const [exportToExcel, { isLoading: isExportingExcel }] = useExportToExcelMutation();
  const [isExporting, setIsExporting] = useState(false);

  const [fetchCompleteFindings] = useLazyGetFindingsQuery();
  const [fetchPartialFindings] = useLazyGetFindingsQuery();
  const [fetchMissingFindings] = useLazyGetFindingsQuery();

  const onExportClick = useCallback(async () => {
    if (!project) {
      toast.error(t('schedule-check.export.error-no-project'));
      return;
    }

    try {
      setIsExporting(true);

      const [completeResult, partialResult, missingResult] = await Promise.all([
        fetchCompleteFindings({
          projectId,
          packageId,
          page: 'all',
          status: FindingStatus.ACTIVE,
          scheduleCheckType: ScheduleCheckType.COMPLETE,
        }).unwrap(),
        fetchPartialFindings({
          projectId,
          packageId,
          page: 'all',
          status: FindingStatus.ACTIVE,
          scheduleCheckType: ScheduleCheckType.PARTIAL,
        }).unwrap(),
        fetchMissingFindings({
          projectId,
          packageId,
          page: 'all',
          status: FindingStatus.ACTIVE,
          scheduleCheckType: ScheduleCheckType.MISSING,
        }).unwrap(),
      ]);

      const excelExport = createScheduleCheckExcelExport(
        {
          completeFindings: completeResult.results as Finding[],
          partialFindings: partialResult.results as Finding[],
          missingFindings: missingResult.results as Finding[],
        },
        t,
        project.name,
      );

      const { base64Data } = await exportToExcel({
        projectId,
        packageId,
        excelExport,
      }).unwrap();

      saveBlobBase64(`${project.name}_schedule-check.xlsx`, base64Data, MIME_TYPES.xlsx);
      toast.success(t('schedule-check.export.success'));
    } catch (error) {
      console.error('Error exporting schedule-check:', error);
      toast.error(t('schedule-check.export.error'));
    } finally {
      setIsExporting(false);
    }
  }, [
    project,
    t,
    exportToExcel,
    projectId,
    packageId,
    fetchCompleteFindings,
    fetchPartialFindings,
    fetchMissingFindings,
  ]);

  const isLoading = isExporting || isExportingExcel;

  return (
    <Button
      rounded
      secondary
      disabled={isLoading}
      onClick={onExportClick}
      icon={isLoading ? () => <Spinner size="xsmall" /> : undefined}
      className={clsx('text-sm font-bold min-h-[36px] whitespace-normal', className)}
    >
      {t('schedule-check.export.button-label')}
    </Button>
  );
};
